import { Component, Input } from '@angular/core';
import { ComponentPortal, Portal } from '@angular/cdk/portal';
import { WebSpeechApiService } from '../library/web-speech-api.service';
import { ControlService } from '../library/control.service';
import { UserVideoService } from '../library/user-video.service';
import { DeviceSettingsService } from '../library/device-settings.service';
import { DeviceSettingsSpeechRecognitionComponent } from '../device-settings/device-settings-speech-recognition/device-settings-speech-recognition.component';
import { Subscription } from 'rxjs';
import { PlayerService } from '../library/player.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AmplitudeTrackingEventPropValues } from 'src/app/utils/enums/amplitude/amplitudeTrackingEventPropValues.enum';
import { CallFlagService } from 'src/app/services/learner/call-flag/call-flag.service';
import { CallStoreService } from '../library/call-store/call-store.service';
import { LoggerService } from 'src/app/services/logger/logger.service';

@Component({
  host: {
    class: 'theme-dark responsive',
  },
  standalone: false,
  selector: 'app-call-practice-visual-aid',
  templateUrl: './call-practice-visual-aid.component.html',
  styleUrls: [
    './call-practice-visual-aid.component.scss',
    '../call-practice/call-practice.component.css',
  ],
})
export class CallPracticeVisualAidComponent {
  @Input() webSpeechApiService: WebSpeechApiService | undefined;
  @Input() controlService: ControlService | undefined;
  @Input() userVideoService: UserVideoService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() practiceCallUuid: string = '';

  errorSubscription!: Subscription;

  selectedPortal: Portal<any> | null = null;
  componentPortal: ComponentPortal<DeviceSettingsSpeechRecognitionComponent> | null =
    null;

  constructor(
    private amplitudeService: AmplitudeService,
    public callFlagService: CallFlagService,
    public callStore: CallStoreService,
    private logger: LoggerService
  ) {}

  ngOnInit() {
    this.callStore.setDisplayPostCall(false);
    // Subscribing to error message, waiting for the error that can emit this in the webSpeechApiService logic
    if (this.webSpeechApiService) {
      this.errorSubscription = this.webSpeechApiService
        .getError()
        .subscribe(() => {
          if (this.componentPortal === null) {
            this.componentPortal = new ComponentPortal(
              DeviceSettingsSpeechRecognitionComponent
            );
          }
          this.selectedPortal = this.componentPortal;
        });
    }

    window.addEventListener('online', () => {
      console.log('You are back online!');
    });
  }

  ngAfterViewInit(): void {
    if (this.deviceSettingsService) {
      this.deviceSettingsService.startAudioLevelDetection();
    }
  }
  trackBySrc(index: number, item: { src: string }) {
    return item.src;
  }

  get hideResultsForLearners(): boolean {
    const call = this.callStore.getCall();
    return !!call?.hideResultsForLearners;
  }

  protected get canPause() {
    return !this.controlService?.isRealTimeCall;
  }

  protected get showHints() {
    return (
      this.callStore.getHints() != '' &&
      !this.controlService?.displayTourHint &&
      (!this.controlService?.isRealTimeCall ||
        (this.controlService?.isRealTimeCall &&
          this.playerService?.canPlayVideo))
    );
  }

  protected get showAvatarSpeakingBorder() {
    return (
      (!this.playerService?.isLoopActive &&
        !this.callStore.getIsPaused() &&
        this.playerService?.canPlayVideo &&
        !this.controlService?.isRealTimeCall) ||
      (this.controlService?.isRealTimeCall &&
        this.callStore.getIsAvatarSpeaking())
    );
  }

  protected get showAvatarSpeakingIndicator() {
    const isIdleAndReadyNonTavus =
      !this.playerService?.isLoopActive &&
      !this.callStore.getIsPaused() &&
      !this.callStore.getIsAvatarThinking() &&
      !this.controlService?.isRealTimeCall;

    const isTavusAndSpeaking =
      this.controlService?.isRealTimeCall &&
      this.callStore.getIsAvatarSpeaking();

    return isIdleAndReadyNonTavus || isTavusAndSpeaking;
  }

  protected get showAvatarLoadingIndicator() {
    return (
      this.callStore.getIsAvatarThinking() &&
      !this.callStore.getIsAvatarSpeaking()
    );
  }

  protected get isLoading() {
    return this.playerService?.showLoadingScreen;
  }

  ngOnDestroy(): void {
    console.log('CallPracticeVisualAidComponent ngOnDestroy triggered.');
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }

    // remove speech recognition validation interval
    if (this.controlService) {
      if (this.controlService.intervalSpeechDetected) {
        clearInterval(this.controlService.intervalSpeechDetected);
      }
    }
    if (this.playerService){
      this.playerService.finalAnswer = '';
      this.callStore.setSpeechTranscriptPartial('');
    }

  }

  copyToClipboard(): void {
    const uuid = this.callStore.getCall()?.practiceCallUuid || '';
    navigator.clipboard.writeText(uuid);
  }

  public sendAmplitudeClickPauseEvent(): void {
    // Sending the Event of Clicking on Pause to Amplitude
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.CALL_CLICK_PAUSE, {
      call_type: this.controlService?.callType,
    });
  }

  public sendAmplitudeClickMuteEvent(): void {
    // Sending the Event of Clicking on Mute to Amplitude
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.CALL_CLICK_MUTE, {
      mute_status: this.callStore.getIsMicOn()
        ? AmplitudeTrackingEventPropValues.MUTE_STATUS_UNMUTED
        : AmplitudeTrackingEventPropValues.MUTE_STATUS_MUTED,
      call_type: this.controlService?.callType,
    });
  }

  public sendAmplitudeClickEndCallEvent(): void {
    // Sending the Event of Clicking on End Call to Amplitude
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.CALL_CLICK_ENDCALL,
      {
        call_type: this.controlService?.callType,
      }
    );
  }

  // Click Handlers

  public handlePauseClick(): void {
    this.sendAmplitudeClickPauseEvent();
    this.controlService?.togglePause();
  }

  public handleMuteClick(): void {
    this.sendAmplitudeClickMuteEvent();
    this.controlService?.toggleMic();
  }

  public handleEndCallClick() {
    this.sendAmplitudeClickEndCallEvent();

    if (!this.controlService) return;
    this.controlService.userInactivity = false;
    this.controlService.endCallVisualAid();
  }

  avatarLoadingError(event: Event, item: any): void {
    const videoElement = event.target as HTMLVideoElement;
    const error = videoElement.error;

    if (error) {
      switch (error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          console.error('Video load aborted by user or browser:', item.src);
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          console.error('Network error while loading video:', item.src);
          break;
        case MediaError.MEDIA_ERR_DECODE:
          console.error('Decoding error while playing video:', item.src);
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          console.error('Video format or source not supported:', item.src);
          break;
        default:
          console.error('Unknown video error occurred:', item.src);
      }
    } else {
      console.error('An unknown error occurred while loading video:', item.src);
    }
  }
}
